/**
 * Markers tool — click-on-canvas to drop a 3D annotation marker.
 *
 * Hit-test strategy (in priority order):
 *   1. Async splat depth pick via `camera.intersect()` — accurate when the
 *      click lands on splat geometry. Best for placing labels ON things in
 *      the scene (a wall, a sculpture, a chair).
 *   2. Walking-plane fallback at Y=0 (matches walk-path-tool). For empty-air
 *      clicks (skybox, behind splats) the marker drops on the floor plane
 *      directly below the click direction, similar to how walk-path anchors
 *      land. Better than nothing.
 *
 * Interaction:
 *   - Left-click  → drop marker (selection auto-snaps to new marker)
 *   - Right-click → remove nearest marker within screen-space radius
 *   - Esc        → deactivate
 *
 * Activation: via ToolManager → fire `tool.markers` to toggle. The tool
 * applies useCapture on pointerdown so the camera controller can't eat the
 * left-click for orbit-start.
 */

import { Container } from '@playcanvas/pcui';
import { Ray, Vec3 } from 'playcanvas';

import type { Events } from '../../events';
import type { Scene } from '../../scene';

const tmpRay = new Ray();
const tmpHit = new Vec3();
const tmpProj = new Vec3();

class MarkersTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const canvas = scene.canvas;
        let active = false;

        /** Walking-plane fallback. Same idea as walk-path-tool: ray-vs-Y=0. */
        const hitFloor = (clientX: number, clientY: number): Vec3 | null => {
            const rect = canvas.getBoundingClientRect();
            const sx = clientX - rect.left;
            const sy = clientY - rect.top;
            scene.camera.getRay(sx, sy, tmpRay);

            const dirY = tmpRay.direction.y;
            if (Math.abs(dirY) < 1e-6) return null;
            const t = (0 - tmpRay.origin.y) / dirY;
            if (t <= 0) return null;

            tmpHit.copy(tmpRay.direction).mulScalar(t).add(tmpRay.origin);
            return tmpHit;
        };

        /** Pick the nearest marker to a screen point, returns id or null. */
        const pickMarker = (clientX: number, clientY: number, pixelRadius = 18): string | null => {
            const m = (scene as any).markers;
            if (!m?.markers?.length) return null;
            const rect = canvas.getBoundingClientRect();
            const sx = clientX - rect.left;
            const sy = clientY - rect.top;

            let bestId: string | null = null;
            let bestD2 = pixelRadius * pixelRadius;
            for (const marker of m.markers) {
                // Project the marker's TOP (stem + cross sits 0.4 above
                // position) since that's the dot the user actually sees.
                tmpProj.set(marker.position.x, marker.position.y + 0.4, marker.position.z);
                scene.camera.worldToScreen(tmpProj, tmpProj);
                const px = tmpProj.x * rect.width;
                const py = tmpProj.y * rect.height;
                const dx = px - sx;
                const dy = py - sy;
                const d2 = dx * dx + dy * dy;
                if (d2 < bestD2) { bestD2 = d2; bestId = marker.id; }
            }
            return bestId;
        };

        const onPointerDown = async (e: PointerEvent) => {
            if (!active) return;
            if (e.button !== 0 && e.button !== 2) return;
            e.stopPropagation();
            e.preventDefault();

            if (e.button === 2) {
                // Right-click: remove marker under cursor.
                const id = pickMarker(e.clientX, e.clientY);
                if (id) events.fire('markers.remove', id);
                return;
            }

            // Left-click: try splat depth pick first.
            const rect = canvas.getBoundingClientRect();
            const nx = (e.clientX - rect.left) / rect.width;
            const ny = (e.clientY - rect.top) / rect.height;

            let pos: Vec3 | null = null;
            try {
                const hit = await scene.camera.intersect(nx, ny);
                if (hit?.position) pos = hit.position;
            } catch (err) {
                // depth pick can fail if no splats — fall through to floor.
            }

            if (!pos) pos = hitFloor(e.clientX, e.clientY);
            if (!pos) return;

            events.fire('markers.add', pos);
        };

        const onContextMenu = (e: MouseEvent) => {
            if (active) e.preventDefault();  // right-click is delete
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (!active) return;
            if (e.key === 'Escape') {
                e.stopPropagation();
                events.fire('tool.deactivate');
            }
        };

        // useCapture on pointerdown so we beat the camera orbit handler.
        this.activate = () => {
            active = true;
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('pointerdown', onPointerDown, true);
            canvas.addEventListener('contextmenu', onContextMenu);
            window.addEventListener('keydown', onKeyDown);
            events.fire('markers.panel.show');
        };

        this.deactivate = () => {
            active = false;
            canvas.style.cursor = '';
            canvas.removeEventListener('pointerdown', onPointerDown, true);
            canvas.removeEventListener('contextmenu', onContextMenu);
            window.removeEventListener('keydown', onKeyDown);
        };
    }
}

export { MarkersTool };
